import { useCallback, useEffect, useRef, useState } from "react";
import { useAppSelector } from "../../../services/redux/hooks/use-selector";
import { selectSkillFields, selectSkillValue } from "../redux/selectors";
import { getUpdatedSkillValue } from "../helpers/getUpdatedSkillsValue";
import { TValueForService } from "../../../services/api/instances/storageApi/types/service_types";
import { useStorageSelectors } from "../../../app/state/storageObjectSlice/hook/use-storage-selectors";
import { TSkillField } from "../types";
import { generateId } from "../../../shared/utils/generateId";
import { isLatinAndStandardChars } from "../../../shared/utils/isLatinAndStandardChars";
import { createNotification } from "../../../app/state/notificationSlice/slice";
import { useAppDispatch } from "../../../services/redux/hooks/use-dispatch";
import { SKILL_BASE_FIELD } from "../constants";
import { useTranslation } from "react-i18next";

export const useSkillsUpdater = (
  saveSkill: (
    value: TValueForService<"skills">,
    withDebounce?: boolean,
    name?: string,
  ) => void,
) => {
  const dispatch = useAppDispatch();
  const [draftValues, setDraftValues] = useState<TSkillField[]>([]);
  const { selectedId } = useStorageSelectors("skills");
  const value = useAppSelector(selectSkillValue);
  const fields = useAppSelector(selectSkillFields);
  const [errorInput, setErrorInput] = useState<boolean>(false);
  const { t } = useTranslation();

  const hasNotifiedRef = useRef(false);
  const previousLabelRef = useRef<Record<string, string>>({});

  useEffect(() => {
    if (!value || !fields) {
      setDraftValues(SKILL_BASE_FIELD);
      previousLabelRef.current = {};
    } else {
      setDraftValues(fields);
    }
  }, [selectedId]);

  const handleChangeInput = useCallback(
    (
      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
      id: string,
      type?: "key" | "value",
    ) => {
      const newValue = event.target.value;
      const currentField = draftValues.find((f) => f.id === id);
      if (!currentField) return;
      if (
        type === "key" &&
        !previousLabelRef.current[id] &&
        currentField.label
      ) {
        previousLabelRef.current[id] = currentField.label;
      }
      const newField =
        type && type === "key"
          ? { ...currentField, label: newValue }
          : { ...currentField, value: newValue };

      setDraftValues((prevDrafts) =>
        prevDrafts.map((item) => (item.id === id ? { ...newField } : item)),
      );
    },
    [draftValues, fields],
  );

  const handleUpdateSkill = useCallback(
    (id: string) => {
      if (!value) return;
      const currentField = draftValues.find((filed) => filed.id === id);
      if (!currentField) return;
      const field =
        currentField.label === t("skills.name_label")
          ? "name"
          : currentField.label === t("skills.description_label")
            ? "description"
            : "instruction";
      let updatedValue = getUpdatedSkillValue(value, {
        [field]: currentField.value,
      });
      saveSkill(updatedValue);
    },
    [value, draftValues, saveSkill],
  );

  const getFieldValue = useCallback(
    (id: string) => {
      const currentField = draftValues.find((v) => v.id === id);
      return currentField ? currentField : null;
    },
    [draftValues, fields],
  );

  const handleSkillFriendlyNameChange = (name: string) => {
    if (!value) return;
    const updatedValue = getUpdatedSkillValue(value, {
      friendly_name: name,
    });
    saveSkill(updatedValue, true, name);
  };

  const handleAddNewField = (type: "child" | "parent") => {
    let newField: TSkillField = {
      id: generateId(),
      label: "",
      value: "",
      placeholder: `${t("skills.input_placeholder")}`,
    };
    if (type === "parent") {
      newField = {
        id: generateId(),
        label: "",
        value: "",
        placeholder: `${t("skills.input_placeholder")}`,
        parent: "metadata",
      };
    }
    setDraftValues([...draftValues, newField]);
  };

  const handleDeleteField = (id: string) => {
    delete previousLabelRef.current[id];
    const field = draftValues.find((f) => f.id === id);
    const newFields = draftValues.filter((f) => f.id !== id);
    setDraftValues(newFields);
    if (!field || !value?.skill_object?.metadata) return;
    const fieldName = field.label;
    if (Object.hasOwn(value?.skill_object?.metadata, fieldName)) {
      const newMetadata = { ...value.skill_object.metadata };
      delete (newMetadata as any)[fieldName];
      const updatedValue = getUpdatedSkillValue(value, {
        metadata: { ...newMetadata },
      });
      saveSkill(updatedValue);
    }
  };

  const handleConfirm = useCallback(
    (id: string) => {
      if (!value) return;
      const field = draftValues.find((f) => f?.id === id);
      if (!field) return;
      const meta = { ...value.skill_object?.metadata };
      const oldLabel = previousLabelRef.current[id];

      if (
        oldLabel &&
        oldLabel !== field.label &&
        Object.hasOwn(meta, oldLabel)
      ) {
        delete (meta as any)[oldLabel];
      }
      let updatedValue = getUpdatedSkillValue(value, {
        metadata: {
          ...meta,
          [field.label]: field.value,
        },
      });

      delete previousLabelRef.current[id];
      setDraftValues((prevDrafts) =>
        prevDrafts.map((item) =>
          item.id === id ? { ...field, inStorageObject: true } : item,
        ),
      );
      saveSkill(updatedValue);
    },
    [draftValues],
  );

  const handleBeforeInput = useCallback(
    (
      e: React.FormEvent<HTMLInputElement | HTMLTextAreaElement> | any,
      id: string,
    ) => {
      const inputData = e.data;

      if (!inputData) return;

      const currentField = draftValues.find((f) => f.id === id);

      if (!currentField || currentField.label !== t("skills.name_label")) {
        return;
      }

      if (!isLatinAndStandardChars(inputData)) {
        e.preventDefault();

        setErrorInput(true);
        if (!hasNotifiedRef.current) {
          hasNotifiedRef.current = true;

          dispatch(
            createNotification("warning", t("skills.input_warning"), 3000),
          );
        }
        return;
      }

      if (hasNotifiedRef.current) {
        hasNotifiedRef.current = false;
      }

      if (errorInput) {
        setErrorInput(false);
      }
    },
    [draftValues, dispatch, errorInput],
  );

  const handlePaste = (e: React.ClipboardEvent, id: string) => {
    setErrorInput(false);
    const currentField = draftValues.find((f) => f.id === id);
    if (currentField && currentField.label !== t("skills.name_label")) return;
    setErrorInput(false);
    const pastedText = e.clipboardData.getData("text");

    if (!isLatinAndStandardChars(pastedText)) {
      setErrorInput(true);
      e.preventDefault();
      dispatch(createNotification("warning", t("skills.input_warning"), 3000));
    }
  };

  return {
    errorInput,
    draftValues,
    handleChangeInput,
    handleUpdateSkill,
    getFieldValue,
    handlePaste,
    handleBeforeInput,
    handleAddNewField,
    handleDeleteField,
    handleConfirm,
    handleSkillFriendlyNameChange,
  };
};
